import { create } from 'zustand'
import { notificationsApi, type NotificationItem } from '@/lib/api'

interface NotificationState {
  notifications: NotificationItem[]
  unreadCount: number
  loading: boolean

  fetchNotifications: () => Promise<void>
  // Apply a server-pushed bell payload (SSE) — same shape as GET /notifications/.
  applySnapshot: (data: { items: NotificationItem[]; unread_count: number }) => void
  markRead: (id: number) => Promise<void>
  markAllRead: () => Promise<void>

  // Reset — wipes per-user data on account switch (see main.tsx auth subscribe).
  reset: () => void
}

const initialState = {
  notifications: [] as NotificationItem[],
  unreadCount: 0,
  loading: false,
}

export const useNotificationStore = create<NotificationState>()((set, get) => ({
  ...initialState,

  fetchNotifications: async () => {
    set({ loading: true })
    try {
      const { data } = await notificationsApi.getAll(20)
      set({ notifications: data.items, unreadCount: data.unread_count, loading: false })
    } catch {
      // Silent — the bell is non-critical; don't toast on every poll failure.
      set({ loading: false })
    }
  },

  applySnapshot: (data) =>
    set({ notifications: data.items, unreadCount: data.unread_count, loading: false }),

  markRead: async (id: number) => {
    // Optimistic: flip locally, then persist.
    const { notifications, unreadCount } = get()
    const target = notifications.find((n) => n.id === id)
    if (!target || target.is_read) return
    set({
      notifications: notifications.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      unreadCount: Math.max(0, unreadCount - 1),
    })
    try {
      await notificationsApi.markRead(id)
    } catch {
      // Re-sync from server on failure.
      get().fetchNotifications()
    }
  },

  markAllRead: async () => {
    const { notifications } = get()
    if (notifications.every((n) => n.is_read)) return
    set({
      notifications: notifications.map((n) => ({ ...n, is_read: true })),
      unreadCount: 0,
    })
    try {
      await notificationsApi.markAllRead()
    } catch {
      get().fetchNotifications()
    }
  },

  reset: () => set(initialState),
}))
